import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderArchive, ArrowLeft, Download, Send, RefreshCw, FileText, Upload,
  CheckCircle, Clock, AlertCircle, XCircle, Eye, Trash2, Plus, Link2,
  Loader2, File, ExternalLink, Calendar, Mail, Building2, MessageSquare, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';

const API = process.env.REACT_APP_BACKEND_URL;

// Status config
const statusConfig = {
  in_progress: { label: 'En préparation', color: 'bg-slate-100 text-slate-700', icon: Clock },
  ready: { label: 'Prêt à envoyer', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  sent: { label: 'Envoyé', color: 'bg-green-100 text-green-700', icon: Send },
  downloaded: { label: 'Téléchargé', color: 'bg-emerald-100 text-emerald-800', icon: Download },
  expired: { label: 'Lien expiré', color: 'bg-red-100 text-red-700', icon: XCircle }
};

// Category labels
const categoryLabels = {
  sales_invoices: 'Factures ventes',
  purchase_invoices: 'Factures achats',
  bank_statements: 'Relevé bancaire',
  sepa_remises: 'Remises SEPA',
  payslips: 'Bulletins salaire',
  manual_files: 'Documents divers'
};

// Format file size
const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// File row component
const FileRow = ({ file, onPreview, onRemove, canRemove }) => {
  const isRecent = file.added_at && (Date.now() - new Date(file.added_at).getTime()) < 24 * 60 * 60 * 1000;
  const isUpdated = file.version && file.version > 1;
  
  return (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-slate-300 transition-colors">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-slate-100 rounded">
          <FileText className="w-4 h-4 text-slate-600" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 text-sm">{file.filename}</span>
            {isRecent && <Badge className="bg-blue-100 text-blue-700 text-xs">Nouveau</Badge>}
            {isUpdated && <Badge className="bg-orange-100 text-orange-700 text-xs">v{file.version}</Badge>}
            {file.source_type === 'manual' && <Badge variant="outline" className="text-xs">Manuel</Badge>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span>{categoryLabels[file.category] || 'Document'}</span>
            <span>{formatSize(file.file_size)}</span>
            {file.added_at && <span>Ajouté le {new Date(file.added_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onPreview(file)}>
          <Eye className="w-4 h-4" />
        </Button>
        {canRemove && (
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => onRemove(file)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Missing document card
const MissingDocCard = ({ doc, onNavigate }) => {
  const moduleLabels = {
    ventes: 'Module Ventes',
    achats: 'Module Achats',
    rh: 'Module RH',
    rapprochement: 'Rapprochement bancaire'
  };
  
  return (
    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
      <div className="flex items-center gap-2 mb-1">
        <XCircle className="w-4 h-4 text-red-500" />
        <span className="font-medium text-red-800 text-sm">{doc.name}</span>
      </div>
      <p className="text-xs text-red-600 mb-2">
        Ce document n'est pas encore dans le dossier
      </p>
      <Button 
        variant="outline" 
        size="sm" 
        className="text-red-700 border-red-300 hover:bg-red-100"
        onClick={() => onNavigate(`/${doc.module}`)}
      >
        <ExternalLink className="w-3 h-3 mr-1" />
        Accéder au {moduleLabels[doc.module] || doc.module}
      </Button>
    </div>
  );
};

export default function ComptabiliteDossier() {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [folderMessage, setFolderMessage] = useState('');
  const [savingMessage, setSavingMessage] = useState(false);
  
  const token = localStorage.getItem('token');

  const fetchFolder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/comptabilite/folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolder(res.data);
      setFolderMessage(res.data.custom_folder_message || '');
    } catch (error) {
      console.error('Error fetching folder:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [folderId, token]);

  useEffect(() => {
    fetchFolder();
  }, [fetchFolder]);

  const handleCollect = async () => {
    setCollecting(true);
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/${folderId}/collect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.collected_count} fichiers collectés`);
      fetchFolder();
    } catch (error) {
      toast.error('Erreur lors de la collecte');
    } finally {
      setCollecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/${folderId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const report = res.data.sync_report || {};
      const messages = [];
      if (res.data.new_files_collected > 0) {
        messages.push(`${res.data.new_files_collected} nouveaux`);
      }
      if (report.updated?.length > 0) {
        messages.push(`${report.updated.length} mis à jour`);
      }
      if (report.deleted?.length > 0) {
        messages.push(`${report.deleted.length} supprimés`);
      }
      if (report.out_of_period?.length > 0) {
        messages.push(`${report.out_of_period.length} hors période retirés`);
      }
      if (messages.length > 0) {
        toast.success(`Synchronisation: ${messages.join(', ')}`);
      } else {
        toast.info('Dossier déjà à jour');
      }
      fetchFolder();
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/${folderId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dossier envoyé à ${res.data.sent_to?.join(', ')}`);
      fetchFolder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleDownload = () => {
    window.open(`${API}/api/comptabilite/folders/${folderId}/download?token=${token}`, '_blank');
  };

  const handleGenerateLink = async () => {
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/${folderId}/generate-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigator.clipboard.writeText(res.data.download_url);
      toast.success('Lien copié dans le presse-papiers');
    } catch (error) {
      toast.error('Erreur lors de la génération du lien');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', uploadDescription);
    
    try {
      await axios.post(
        `${API}/api/comptabilite/folders/${folderId}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Fichier ajouté');
      setUploadDescription('');
      fetchFolder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveFile = async (file) => {
    try {
      await axios.delete(
        `${API}/api/comptabilite/folders/${folderId}/files/${file.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fichier supprimé');
      fetchFolder();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePreviewFile = (file) => {
    // Open file in new tab for preview
    const previewUrl = `${API}/api/comptabilite/folders/${folderId}/files/${file.id}/preview?token=${token}`;
    window.open(previewUrl, '_blank');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Trigger upload with dropped file
      const dt = new DataTransfer();
      dt.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
        handleFileUpload({ target: { files: dt.files } });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!folder) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FolderArchive className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Dossier non trouvé</p>
          <Button variant="outline" onClick={() => navigate('/comptabilite')} className="mt-4">
            Retour
          </Button>
        </div>
      </div>
    );
  }

  const status = statusConfig[folder.status] || statusConfig.in_progress;
  const StatusIcon = status.icon;
  const allFiles = [...(folder.files || []), ...(folder.manual_files || []).map(f => ({ ...f, category: 'manual_files', source_type: 'manual' }))];
  const missingDocs = folder.missing_documents || [];
  const canModify = !['sent', 'downloaded', 'expired'].includes(folder.status);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/comptabilite')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <FolderArchive className="w-6 h-6 text-blue-500" />
              Dossier {folder.display_name}
              <Badge className={status.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {folder.sent_at ? (
                <>Envoyé le {new Date(folder.sent_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}</>
              ) : folder.send_date ? (
                <>Envoi prévu le {new Date(folder.send_date).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}</>
              ) : (
                <>Créé le {new Date(folder.created_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}</>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSync} disabled={syncing} title="Synchronise les modifications des modules sources">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Synchroniser
          </Button>
          <Button variant="outline" onClick={handleCollect} disabled={collecting || !canModify}>
            {collecting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Collecter
          </Button>
          <Button variant="outline" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-1" />
            Télécharger ZIP
          </Button>
          <Button variant="outline" onClick={handleGenerateLink}>
            <Link2 className="w-4 h-4 mr-1" />
            Générer lien
          </Button>
          <Button onClick={handleSend} disabled={sending} className="bg-blue-600 hover:bg-blue-700">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            {folder.status === 'sent' ? 'Renvoyer' : 'Envoyer'}
          </Button>
        </div>
      </div>

      {/* Summary stats - Résumé par catégorie en haut */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">
              {allFiles.filter(f => f.category === 'sales_invoices').length}
            </div>
            <div className="text-xs text-blue-600">Factures ventes</div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-orange-700">
              {allFiles.filter(f => f.category === 'purchase_invoices').length}
            </div>
            <div className="text-xs text-orange-600">Factures achats</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-green-700">
              {allFiles.filter(f => f.category === 'bank_statements').length}
            </div>
            <div className="text-xs text-green-600">Relevés bancaires</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-purple-700">
              {allFiles.filter(f => f.category === 'payslips').length}
            </div>
            <div className="text-xs text-purple-600">Bulletins salaire</div>
          </CardContent>
        </Card>
        <Card className="bg-cyan-50 border-cyan-200">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-cyan-700">
              {allFiles.filter(f => f.category === 'sepa_remises').length}
            </div>
            <div className="text-xs text-cyan-600">Remises SEPA</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-3 text-center">
            <div className="text-2xl font-bold text-slate-700">
              {allFiles.filter(f => f.category === 'manual_files').length}
            </div>
            <div className="text-xs text-slate-600">Documents divers</div>
          </CardContent>
        </Card>
      </div>

      {/* Infos supplémentaires */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{allFiles.length}</div>
            <div className="text-sm text-slate-500">Fichiers</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{formatSize(folder.total_size || 0)}</div>
            <div className="text-sm text-slate-500">Taille totale</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600">{missingDocs.length}</div>
            <div className="text-sm text-slate-500">Documents manquants</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{folder.download_count || 0}</div>
            <div className="text-sm text-slate-500">Téléchargements</div>
          </CardContent>
        </Card>
      </div>

      {/* Message pour l'expert-comptable */}
      <Card data-testid="folder-message-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-5 h-5 text-blue-500" />
            Message du mois
          </CardTitle>
          <CardDescription>
            Ce message sera inclus dans le corps de l'email envoyé à l'expert-comptable (variable {'{message}'})
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            data-testid="folder-message-textarea"
            value={folderMessage}
            onChange={(e) => setFolderMessage(e.target.value)}
            placeholder="Ex : Attention ce mois-ci, deux factures d'achat sont en attente de réception. Merci de ne pas clôturer la TVA avant..."
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              data-testid="save-folder-message-btn"
              size="sm"
              variant="outline"
              disabled={savingMessage || folderMessage === (folder.custom_folder_message || '')}
              onClick={async () => {
                setSavingMessage(true);
                try {
                  await axios.put(
                    `${API}/api/comptabilite/folders/${folderId}/message`,
                    { message: folderMessage },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setFolder({ ...folder, custom_folder_message: folderMessage.trim() });
                  toast.success('Message enregistré — il sera inclus dans le prochain envoi');
                } catch (error) {
                  toast.error('Erreur lors de l enregistrement du message');
                } finally {
                  setSavingMessage(false);
                }
              }}
            >
              {savingMessage ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Enregistrer le message
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Files present - organized by category */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-500" />
              Fichiers dans le dossier ({allFiles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {allFiles.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <FileText className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">Aucun fichier collecté</p>
                <Button variant="outline" size="sm" onClick={handleCollect} className="mt-3" disabled={collecting}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Collecter les fichiers
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-4">
                  {/* Group files by category */}
                  {['sales_invoices', 'purchase_invoices', 'bank_statements', 'sepa_remises', 'payslips', 'manual_files'].map(category => {
                    const categoryFiles = allFiles.filter(f => f.category === category);
                    if (categoryFiles.length === 0) return null;
                    
                    return (
                      <div key={category} className="space-y-2">
                        <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-100 rounded-lg sticky top-0 z-10">
                          <FolderArchive className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-slate-700 text-sm">
                            {categoryLabels[category] || category}
                          </span>
                          <Badge variant="secondary" className="ml-auto text-xs">
                            {categoryFiles.length}
                          </Badge>
                        </div>
                        <div className="space-y-1 pl-2">
                          {categoryFiles.map((file, idx) => (
                            <FileRow
                              key={file.id || `${category}-${idx}`}
                              file={file}
                              onPreview={handlePreviewFile}
                              onRemove={handleRemoveFile}
                              canRemove={canModify && file.source_type === 'manual'}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Missing documents + Upload */}
        <div className="space-y-6">
          {/* Missing documents */}
          {missingDocs.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircle className="w-4 h-4 text-orange-500" />
                  Documents manquants ({missingDocs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {missingDocs.slice(0, 5).map((doc, idx) => (
                    <MissingDocCard key={idx} doc={doc} onNavigate={navigate} />
                  ))}
                  {missingDocs.length > 5 && (
                    <p className="text-sm text-slate-500 text-center">
                      Et {missingDocs.length - 5} autre(s)...
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Manual upload */}
          {canModify && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Upload className="w-4 h-4 text-purple-500" />
                  Ajouter un fichier
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.csv,.docx"
                    onChange={handleFileUpload}
                  />
                  {uploading ? (
                    <Loader2 className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-2" />
                  ) : (
                    <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  )}
                  <p className="text-sm text-slate-600">
                    Glissez un fichier ici ou cliquez pour parcourir
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    PDF, JPG, PNG, XLSX, CSV, DOCX • Max 20 Mo
                  </p>
                </div>
                
                <div className="mt-3">
                  <Input
                    placeholder="Description (optionnel)"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
